import React, { useState, useEffect, useRef, useCallback } from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import './_gallery.scss';

const Gallery = ({ gallery}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const viewportRef = useRef(null);
  const containerRef = useRef(null);

  const images = gallery?.images || [];
  const totalImages = images.length;

  const [slidesToShow, setSlidesToShow] = useState(3);
  const maxIndex = Math.max(0, totalImages - slidesToShow);

  useEffect(() => {
    const updateSlidesToShow = () => {
      if (window.innerWidth <= 960) {
        setSlidesToShow(1);
      } else {
        setSlidesToShow(3);
      }
    };

    updateSlidesToShow();
    window.addEventListener('resize', updateSlidesToShow);
    return () => window.removeEventListener('resize', updateSlidesToShow);
  }, []);

  useEffect(() => {
    const newMaxIndex = Math.max(0, totalImages - slidesToShow);
    if (currentIndex > newMaxIndex) {
      setCurrentIndex(newMaxIndex);
    }
  }, [slidesToShow, totalImages, currentIndex]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    if (currentIndex < maxIndex) {
      setCurrentIndex(currentIndex + 1);
    }
  }, [currentIndex, maxIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPrevious();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNext();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
      return () => {
        container.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [goToPrevious, goToNext]);

  // Touch/Mouse drag handlers
  const handleStart = (clientX) => {
    setIsDragging(true);
    setStartX(clientX);
    setTranslateX(0);
  };

  const handleMove = useCallback((clientX) => {
    if (!isDragging) return;
    
    const diffX = clientX - startX;
    
    // Limit drag distance
    const maxDrag = window.innerWidth * 0.3;
    const limitedDiffX = Math.max(-maxDrag, Math.min(maxDrag, diffX));
    
    setTranslateX(limitedDiffX);
  }, [isDragging, startX]);

  const handleEnd = useCallback(() => {
    if (!isDragging) return;
    
    const threshold = 50;
    
    if (Math.abs(translateX) > threshold) {
      if (translateX > 0 && currentIndex > 0) {
        goToPrevious();
      } else if (translateX < 0 && currentIndex < maxIndex) {
        goToNext();
      }
    }
    
    setIsDragging(false);
    setStartX(0);
    setTranslateX(0);
  }, [isDragging, translateX, currentIndex, maxIndex, goToPrevious, goToNext]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    handleStart(e.clientX);
  };

  const handleMouseMove = useCallback((e) => {
    handleMove(e.clientX);
  }, [handleMove]);

  const handleMouseUp = useCallback(() => {
    handleEnd();
  }, [handleEnd]);

  const handleTouchStart = (e) => {
    handleStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    handleMove(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    handleEnd();
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const getTransform = () => {
    const slideWidth = 100 / slidesToShow;
    const baseTransform = -(currentIndex * slideWidth);
    const dragOffset = isDragging ? (translateX / viewportRef.current?.offsetWidth) * 100 : 0;
    return `translateX(${baseTransform + dragOffset}%)`;
  };

  const isSlideActive = (slideIndex) => {
    return slideIndex >= currentIndex && slideIndex < currentIndex + slidesToShow;
  };

  if (!images || images.length === 0) {
    return null;
  }

  return (
    <section 
      ref={containerRef}
      className={`gallery media`} 
      aria-label="Gallery"
    >
      <div 
        ref={viewportRef}
        className="gallery__viewport"
        role="button"
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div 
          className="gallery__track"
          style={{
            transform: getTransform(),
            transition: isDragging ? 'none' : 'transform 0.3s ease-in-out'
          }}
          role="group" 
          aria-label="Gallery image slider"
        >
          {images.map((mediaFragment, index) => {
            const media = mediaFragment.media;
            const mediaResponsive = mediaFragment.mediaResponsive;
            const gatsbyImage = getImage(media);
            const gatsbyImageResponsive = mediaResponsive ? getImage(mediaResponsive) : null;
            
            return (
              <div 
                key={`gallery-slide-${index}-${mediaFragment.id || media?.id || ''}`} 
                className={`gallery__slide ${isSlideActive(index) ? 'gallery__slide_active' : ''}`}
                style={{ width: `${100 / slidesToShow}%` }}
              >
                {gatsbyImage ? (
                  <GatsbyImage
                    image={gatsbyImage}
                    alt={media.title || mediaFragment.description || `Gallery image ${index + 1}`}
                    loading={index < slidesToShow ? 'eager' : 'lazy'}
                    className={`gallery__image ${gatsbyImageResponsive ? 'show-for-large-only' : ''}`}
                  />
                ) : (
                  <img
                    src={media.file?.url}
                    alt={media.title || mediaFragment.description || `Gallery image ${index + 1}`}
                    loading={index < slidesToShow ? 'eager' : 'lazy'}
                    className={`gallery__image ${gatsbyImageResponsive ? 'show-for-large-only' : ''}`}
                  />
                )}
                
                {gatsbyImageResponsive && (
                  <GatsbyImage
                    image={gatsbyImageResponsive}
                    alt={mediaResponsive.title || mediaFragment.description || `Gallery image ${index + 1} (mobile)`}
                    loading={index < slidesToShow ? 'eager' : 'lazy'}
                    className="gallery__image gallery__image--responsive show-for-small-only"
                  />
                )}
                
                {!gatsbyImageResponsive && mediaResponsive?.file?.url && (
                  <img
                    src={`${mediaResponsive.file.url}?w=960`}
                    alt={mediaResponsive.title || mediaFragment.description || `Gallery image ${index + 1} (mobile)`}
                    loading={index < slidesToShow ? 'eager' : 'lazy'}
                    className="gallery__image gallery__image--responsive show-for-small-only"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
      <nav className="gallery__nav" role="navigation" aria-label="Gallery navigation">
        <button
          className={`gallery__nav-btn gallery__nav-btn--prev ${currentIndex === 0 ? 'gallery__nav-btn--disabled' : ''}`}
          onClick={goToPrevious}
          disabled={currentIndex === 0}
          aria-label="Previous image"
          type="button"
        >
          <span className="gallery__nav-icon gallery__nav-icon--prev" aria-hidden="true" />
        </button>
        
        <button
          className={`gallery__nav-btn gallery__nav-btn--next ${currentIndex >= maxIndex ? 'gallery__nav-btn--disabled' : ''}`}
          onClick={goToNext}
          disabled={currentIndex >= maxIndex}
          aria-label="Next image"
          type="button"
        >
          <span className="gallery__nav-icon gallery__nav-icon--next" aria-hidden="true" />
        </button>
      </nav>
    </section>
  );
};

export default Gallery; 